/**
 * PlazaShowcase - 首页「社区精选 3D 模型」展示区块。
 *
 * 落地页叙事顺序：Hero →【本区块】→ Why Tsuvi → HowItWorks → UseCases → …
 * 在主张之后立刻出示社区真实成品——"先看做得怎么样"。
 *
 * 设计要点：
 *  - 数据走 listPlaza({ sort: "popular", limit: 8 })，首屏只取 8 条热门模型。
 *  - 复用 ModelCard + plaza-grid 样式，与社区广场页视觉/交互完全一致。
 *  - 点击卡片懒加载 WorkPreviewModal（含 three.js，约 1MB），与广场页同款。
 *  - 失败/空数据时静默降级：区块只渲染「浏览全部」入口，不留空白破洞，
 *    不抛错给用户——展示区是锦上添花，不应阻塞首屏其余内容。
 */

import { useState, useEffect, useCallback, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import {
  listPlaza,
  PLAZA_DOWNLOAD_COST,
  type PlazaModelSummary,
} from "../lib/api";
import ModelCard from "./ModelCard";
import { prefetchGlb } from "../lib/modelCache";
import { LocalizedLink } from "../lib/lang";

// 懒加载预览弹窗（含 three.js / R3F，约 1MB），点击卡片时才拉入。
// lazy 与交互预热共享同一 import 工厂，确保两者命中同一模块实例。
const importModal = () => import("./WorkPreviewModal");
const WorkPreviewModal = lazy(importModal);

export default function PlazaShowcase() {
  const { t } = useTranslation();
  // null = 加载中（渲染骨架占位）；[] = 已加载且广场为空（不占位）
  const [items, setItems] = useState<PlazaModelSummary[] | null>(null);
  const [previewModel, setPreviewModel] = useState<PlazaModelSummary | null>(
    null,
  );

  // 首次真实交互（pointerdown/keydown）才预热 3D 预览 chunk（不预拉 GLB，流量友好），
  // 首点省去最重串行等待。区块位于 hero 正下方、接近首屏，IntersectionObserver 会在
  // 页面加载即触发，让 Lighthouse/PSI 这类「加载后不交互」的访客白下 1MB+ three
  // chunk（Reduce unused JavaScript 审计直接命中）；真实交互信号在无交互审计中不会触发。
  // 测试环境跳过：预热的动态 import 会绕过 vi.mock 对懒加载组件的拦截，
  // 导致测试加载真实 three.js（jsdom 无 ResizeObserver 直接崩）。
  useEffect(() => {
    if (import.meta.env.MODE === "test") return;
    const warm = () => {
      window.removeEventListener("pointerdown", warm);
      window.removeEventListener("keydown", warm);
      void importModal();
    };
    window.addEventListener("pointerdown", warm);
    window.addEventListener("keydown", warm);
    return () => {
      window.removeEventListener("pointerdown", warm);
      window.removeEventListener("keydown", warm);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const result = await listPlaza({ sort: "popular", limit: 8 });
      setItems(result.items);
    } catch (err) {
      // 静默降级：展示区是锦上添花，失败不阻塞首屏，仅控制台告警便于排障。
      console.warn("PlazaShowcase load failed", err);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section id="community" className="landing-showcase">
      <span className="eyebrow">{t("landing.sectionBadges.showcase")}</span>
      <h2 className="section-title">{t("landing.showcase.title")}</h2>
      <p className="section-subtitle">{t("landing.showcase.subtitle")}</p>

      {/* 加载中：骨架卡片占位（与真实卡片同构：aspect 1/1 缩略图 + 信息区），
          预留网格高度，避免数据到达后插入 8 张卡片把下方区块推下去（CLS）。 */}
      {items === null && (
        <div className="plaza-grid" aria-hidden>
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="model-card skeleton-card">
              <div className="model-card-thumb" />
              <div className="model-card-info skeleton-info">
                <div className="skeleton-line" />
                <div className="skeleton-line skeleton-line-short" />
              </div>
            </div>
          ))}
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="plaza-grid">
          {items.map((model, i) => (
            <ModelCard
              key={model.id}
              model={model}
              /* 前 4 张首屏可见（移动端 2×2 / 桌面第一行），缩略图高优先加载
                 （首屏缩略图是落地页 LCP 元素，lazy 会被浏览器降级拖慢 LCP） */
              priority={i < 4}
              onOpen={(m) => setPreviewModel(m)}
              onHover={(m) =>
                prefetchGlb(`/api/plaza/${m.id}/preview`)
              }
            />
          ))}
        </div>
      )}

      <div className="showcase-more">
        <LocalizedLink to="/plaza" className="showcase-more-btn">
          {t("landing.showcase.browseAll")}
        </LocalizedLink>
      </div>

      {previewModel && (
        <Suspense
          fallback={<PreviewModalFallback onClose={() => setPreviewModel(null)} />}
        >
          {/* 预览走公开免费的 /preview；下载走计费的 /download（与广场页一致，
              含 downloadCost 扣积分提示，两处弹窗样式/提示完全一致） */}
          <WorkPreviewModal
            glbUrl={`/api/plaza/${previewModel.id}/preview`}
            downloadUrl={`/api/plaza/${previewModel.id}/download?format=glb`}
            downloadCost={PLAZA_DOWNLOAD_COST}
            downloadName={previewModel.id}
            onClose={() => setPreviewModel(null)}
          />
        </Suspense>
      )}
    </section>
  );
}

/**
 * 预览弹窗的 Suspense fallback：chunk 加载期间显示 loading 遮罩。
 * 复用 WorkPreviewModal 的 overlay/preview-modal 样式，视觉无缝衔接。
 * 与 PlazaPage 的 PreviewModalFallback 同款。
 */
function PreviewModalFallback({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="preview-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("preview.title")}
      onClick={onClose}
    >
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="model-viewer loading-screen">
          <div className="loading-spinner" />
          <p>{t("common.loadingViewer")}</p>
        </div>
      </div>
    </div>
  );
}
